/**
 * Utility functions for authentication and user data
 */

// Import for debug function
import { journalEntriesAPI } from '../lib/api';
import { getJournalEntries, deleteJournalEntry } from '../services/journalService';

/**
 * Get the current authenticated user from localStorage
 * @returns {Object|null} User object or null if not authenticated
 */
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('current_user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing current user:', error);
    return null;
  }
};

/**
 * Get the current user's ID
 * @returns {string|null} User ID or null if not available
 */
export const getCurrentUserId = () => {
  const user = getCurrentUser();
  return user?.id || user?.userId || user?.Id || null;
};

/**
 * Get the current user's company ID
 * @returns {string|null} Company ID or null if not available
 */
export const getCurrentCompanyId = () => {
  const user = getCurrentUser();
  const companyId = user?.companyId || null;
  console.log('🏢 getCurrentCompanyId - user:', user);
  console.log('🏢 getCurrentCompanyId - companyId:', companyId);
  return companyId;
};

/**
 * Get the current company logo URL
 * @returns {string|null} Logo URL or null if not available
 */
export const getCurrentCompanyLogo = () => {
  const company = getCurrentCompany();
  const user = getCurrentUser();
  return company?.logo || company?.logoUrl || user?.companyLogo || null;
};

/**
 * Get the current company information
 * @returns {Object|null} Company object or null if not available
 */
export const getCurrentCompany = () => {
  const user = getCurrentUser();
  return user?.company || null;
};

/**
 * Get the current company name
 * @returns {string} Company name or default
 */
export const getCurrentCompanyName = () => {
  const company = getCurrentCompany();
  const user = getCurrentUser();
  return company?.name || company?.companyName || user?.companyName || 'Your Company';
};

/**
 * Get the current company address
 * @returns {Object|null} Company address object or null
 */
export const getCurrentCompanyAddress = () => {
  const company = getCurrentCompany();
  if (!company?.address) return null;
  
  // Handle both object format and string format
  if (typeof company.address === 'string') {
    // Parse address string format like "Nairobi, Nairobi, Nairobi, Nairobi, 00100, Kenya"
    const parts = company.address.split(', ').map(part => part.trim());
    return {
      street: parts[0] || '',
      city: parts[1] || parts[0] || '',
      state: parts[2] || parts[1] || '',
      zipCode: parts[4] || '',
      country: parts[5] || parts[parts.length - 1] || '',
      email: company.email || '',
      phone: company.phone || ''
    };
  }
  
  // Return object format as-is
  return {
    ...company.address,
    email: company.address.email || company.email || '',
    phone: company.address.phone || company.phone || ''
  };
};

/**
 * Get formatted company address string
 * @returns {string} Formatted address or empty string
 */
export const getFormattedCompanyAddress = () => {
  const address = getCurrentCompanyAddress();
  if (!address) return '';
  
  const parts = [];
  if (address.street) parts.push(address.street);
  
  const cityStateZip = [];
  if (address.city) cityStateZip.push(address.city);
  if (address.state) cityStateZip.push(address.state);
  if (address.zipCode) cityStateZip.push(address.zipCode);
  if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
  
  if (address.country) parts.push(address.country);
  
  return parts.join('\n');
};

/**
 * Get the current company currency
 * @returns {string|null} Currency code or null
 */
export const getCurrentCompanyCurrency = () => {
  const company = getCurrentCompany();
  const user = getCurrentUser();
  
  // Try multiple possible field names for currency
  const currency = company?.currency || 
                  company?.currencyCode || 
                  user?.currency || 
                  user?.companyCurrency || 
                  user?.currencyCode ||
                  null;
                  
  console.log('🏢 getCurrentCompanyCurrency - company:', company);
  console.log('🏢 getCurrentCompanyCurrency - user currency fields:', {
    companyCurrency: company?.currency,
    companyCurrencyCode: company?.currencyCode,
    userCurrency: user?.currency,
    userCompanyCurrency: user?.companyCurrency,
    userCurrencyCode: user?.currencyCode
  });
  console.log('🏢 getCurrentCompanyCurrency - final currency:', currency);
  
  return currency;
};

/**
 * Get the authentication token
 * @returns {string|null} Auth token or null if not available
 */
export const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = () => {
  const user = getCurrentUser();
  const token = getAuthToken();
  return !!(user && token);
};

/**
 * Get user's full name
 * @returns {string} User's full name or 'User'
 */
export const getCurrentUserName = () => {
  const user = getCurrentUser();
  return user?.fullName || user?.name || 'User';
};

/**
 * Get user's role
 * @returns {string} User's role or 'user'
 */
export const getCurrentUserRole = () => {
  const user = getCurrentUser();
  return user?.role || 'user';
};

/**
 * Update current user data in localStorage
 * @param {Object} updates - Updates to apply to user data
 */
export const updateCurrentUser = (updates) => {
  try {
    const user = getCurrentUser();
    if (user) {
      const updatedUser = { ...user, ...updates };
      localStorage.setItem('current_user', JSON.stringify(updatedUser));
    }
  } catch (error) {
    console.error('Error updating current user:', error);
  }
};

/**
 * Set company data for current user
 * @param {Object} companyData - Company data to set
 */
export const setCurrentCompanyData = (companyData) => {
  try {
    const user = getCurrentUser();
    if (user) {
      const updatedUser = { 
        ...user, 
        company: companyData,
        companyId: companyData.id || companyData.companyId || user.companyId,
        currency: companyData.currency || user.currency
      };
      localStorage.setItem('current_user', JSON.stringify(updatedUser));
    }
  } catch (error) {
    console.error('Error setting company data:', error);
  }
};

/**
 * Clear all authentication data and logout user
 */
export const logout = () => {
  try {
    // Clear all auth-related data from localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    
    // Clear any other auth-related items if they exist
    localStorage.removeItem('user_profile');
    localStorage.removeItem('company_data');
    
    console.log('User logged out - all auth data cleared');
    
    // Redirect to login page if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

/**
 * Check if the current token is valid (exists and not expired)
 * @returns {boolean} True if token appears valid
 */
export const isTokenValid = () => {
  const token = getAuthToken();
  const user = getCurrentUser();
  
  // Basic check - token and user must exist
  if (!token || !user) {
    return false;
  }
  
  // Check if token is just a placeholder or obviously invalid
  if (token === 'null' || token === 'undefined' || token.length < 10) {
    return false;
  }
  
  // TODO: Add token expiration check if your tokens include expiry
  // For JWT tokens, you could decode and check the exp claim:
  // try {
  //   const payload = JSON.parse(atob(token.split('.')[1]));
  //   const now = Math.floor(Date.now() / 1000);
  //   return payload.exp > now;
  // } catch (error) {
  //   return false;
  // }
  
  // For now, we rely on the server to validate the token
  return true;
};

/**
 * Force logout if token is invalid or missing
 * This can be called from anywhere in the app
 */
export const validateTokenAndLogout = () => {
  if (!isTokenValid()) {
    console.warn('Invalid or missing token detected - forcing logout');
    logout();
    return false;
  }
  return true;
};

/**
 * Test function to simulate token removal (for testing purposes)
 * This can be called from browser console: window.testLogout()
 */
export const testTokenRemoval = () => {
  console.log('🧪 Testing token removal and automatic logout...');
  localStorage.removeItem('auth_token');
  console.log('✅ Token removed. The app should automatically logout within 60 seconds or on next API call.');
  
  // Force immediate validation
  setTimeout(() => {
    validateTokenAndLogout();
  }, 1000);
};

// Debug function for testing journal entry deletion
export const debugDeleteJournalEntry = async (entryId) => {
  console.log('🗑️ DEBUG: Testing journal entry deletion...');
  
  try {
    if (!entryId) {
      console.error('❌ No entry ID provided');
      return;
    }
    
    console.log('🗑️ Attempting to delete entry:', entryId);
    const result = await deleteJournalEntry(entryId);
    console.log('✅ Delete result:', result);
    
  } catch (error) {
    console.error('❌ Delete test error:', error);
  }
};

// Debug function for testing currency formatting
export const debugCurrency = () => {
  console.log('💰 DEBUG: Testing currency formatting...');
  
  try {
    const user = getCurrentUser();
    console.log('👤 Current user:', user);
    
    const company = getCurrentCompany();
    console.log('🏢 Current company:', company);
    
    const currency = getCurrentCompanyCurrency();
    console.log('💰 Company currency:', currency);
    
    // Test formatting
    const { formatCurrency } = require('../utils/currency');
    const testAmount = 1234.56;
    const formatted = formatCurrency(testAmount);
    console.log('💰 Formatted amount:', formatted);
    
  } catch (error) {
    console.error('❌ Currency debug error:', error);
  }
};

// Debug function for testing journal entries API
export const debugJournalEntries = async () => {
  console.log('🔍 DEBUG: Starting journal entries debug...');
  
  try {
    const user = getCurrentUser();
    console.log('👤 Current user:', user);
    
    const companyId = getCurrentCompanyId();
    console.log('🏢 Company ID:', companyId);
    
    const token = getAuthToken();
    console.log('🔑 Auth token exists:', !!token);
    console.log('🔑 Auth token length:', token?.length);
    
    if (!companyId) {
      console.error('❌ No company ID found!');
      return;
    }
    
    console.log('🌐 Making direct API call...');
    const response = await journalEntriesAPI.getAll(companyId);
    console.log('📡 Direct API response:', response);
    
    console.log('🔄 Testing service function...');
    const serviceResult = await getJournalEntries();
    console.log('📊 Service result:', serviceResult);
    
  } catch (error) {
    console.error('❌ Debug error:', error);
    console.error('❌ Error response:', error.response?.data);
  }
};

// Make debug functions available globally
if (typeof window !== 'undefined') {
  window.testLogout = testTokenRemoval;
  window.debugJournalEntries = debugJournalEntries;
  window.debugCurrency = debugCurrency;
  window.debugDeleteJournalEntry = debugDeleteJournalEntry;
}